/**
 * components/procurement/PricingSection.jsx — Contract Pricing (per type).
 *
 * The view adapts to the contract type:
 *   • fixed        → single all-in rate
 *   • index        → index reference + adder + % index exposure
 *   • block_index  → block rate + index formula + monthly schedule table
 *                    + Block-and-Index analytics (hedge mix, spend mix)
 * Every field is always editable.
 */
import React, { useState, useEffect } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, CartesianGrid,
} from 'recharts';
import { toast } from 'sonner';
import { Save, Plus, Trash2, DollarSign, TrendingUp, TrendingDown, Radio } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { patchContract, getMarketFeeds } from '../../api/contracts';

const num = (v) => (v === '' || v == null ? null : Number(v));
const compact = (n) => {
  if (n == null || isNaN(n)) return '0';
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return Math.round(n).toLocaleString();
};

const NO_FEED = '__none__';

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
    {children}
  </div>
);

/** Live market-rate chip shown next to a linked index reference. */
const LiveFeedChip = ({ feed }) => {
  if (!feed) return null;
  if (!feed.has_data) {
    return <span className="text-xs text-muted-foreground">· feed pending</span>;
  }
  const up = (feed.change_pct ?? 0) >= 0;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`pricing-live-${feed.symbol}`}>
      <Radio className="w-3 h-3 text-emerald-600 animate-pulse" />
      <span className="font-semibold">{feed.currency === 'USD' ? '$' : ''}{feed.latest_value}</span>
      <span className="text-emerald-700/70">{feed.unit}</span>
      {feed.change_pct != null && (
        <span className={`flex items-center ${up ? 'text-emerald-600' : 'text-red-600'}`}>
          {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {Math.abs(feed.change_pct)}%
        </span>
      )}
    </span>
  );
};

export function PricingSection({ accountId, contract, canEdit, onChanged }) {
  const type = contract.contract_type;
  const p = contract.pricing || {};
  const [fixed, setFixed] = useState({ rate: p.fixed?.rate ?? '', uom: p.fixed?.uom || '$/kWh' });
  const [index, setIndex] = useState({ index_ref: p.index?.index_ref || '', index_feed_symbol: p.index?.index_feed_symbol || '', adder: p.index?.adder ?? '', pct_index_exposure: p.index?.pct_index_exposure ?? '', uom: p.index?.uom || '$/kWh' });
  const [bi, setBi] = useState({
    block_rate: p.block_index?.block_rate ?? '',
    index_ref: p.block_index?.index_ref || '',
    index_feed_symbol: p.block_index?.index_feed_symbol || '',
    index_adder: p.block_index?.index_adder ?? '',
    schedule: p.block_index?.schedule || [],
  });
  const [saving, setSaving] = useState(false);
  const [feeds, setFeeds] = useState([]);

  useEffect(() => {
    if (!accountId || type === 'fixed') return;
    getMarketFeeds({ accountId }).then((d) => setFeeds(d.feeds || [])).catch(() => {});
  }, [accountId, type]);

  // feeds relevant to this contract's commodity (gas/electricity; dual → both)
  const relevantFeeds = feeds.filter((f) => {
    if (contract.commodity === 'dual') return ['gas', 'electricity'].includes(f.commodity);
    return f.commodity === contract.commodity;
  });
  const feedBySymbol = (sym) => feeds.find((f) => f.symbol === sym);

  const onPickFeed = (setter) => (sym) => {
    if (sym === NO_FEED) {
      setter((s) => ({ ...s, index_feed_symbol: '' }));
      return;
    }
    const f = feedBySymbol(sym);
    setter((s) => ({ ...s, index_feed_symbol: sym, index_ref: f ? f.name : s.index_ref }));
  };

  const save = async () => {
    setSaving(true);
    let pricing = {};
    if (type === 'fixed') pricing = { fixed: { rate: num(fixed.rate), uom: fixed.uom } };
    else if (type === 'index') pricing = { index: { index_ref: index.index_ref, index_feed_symbol: index.index_feed_symbol || null, adder: num(index.adder), pct_index_exposure: num(index.pct_index_exposure), uom: index.uom } };
    else pricing = { block_index: {
      block_rate: num(bi.block_rate), index_ref: bi.index_ref, index_feed_symbol: bi.index_feed_symbol || null, index_adder: num(bi.index_adder),
      schedule: bi.schedule.map((r) => ({ month: r.month, hedge_pct: num(r.hedge_pct), block_volume: num(r.block_volume), index_volume: num(r.index_volume), total: num(r.total) })),
    } };
    try {
      await patchContract({ accountId, id: contract.id, patch: { pricing } });
      toast.success('Pricing saved');
      onChanged?.();
    } catch (e) {
      toast.error('Save failed', { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const FeedSelect = ({ value, onChange }) => (
    <Select value={value || NO_FEED} onValueChange={onChange} disabled={!canEdit}>
      <SelectTrigger data-testid="pricing-feed-select"><SelectValue placeholder="Link a live market feed" /></SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_FEED}>No live feed</SelectItem>
        {relevantFeeds.map((f) => (
          <SelectItem key={f.symbol} value={f.symbol}>{f.hub} — {f.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const addRow = () => setBi((s) => ({ ...s, schedule: [...s.schedule, { month: '', hedge_pct: '', block_volume: '', index_volume: '', total: '' }] }));
  const setRow = (i, k, v) => setBi((s) => ({ ...s, schedule: s.schedule.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const delRow = (i) => setBi((s) => ({ ...s, schedule: s.schedule.filter((_, idx) => idx !== i) }));

  // Block & Index analytics
  const sched = bi.schedule || [];
  const totBlock = sched.reduce((a, r) => a + (Number(r.block_volume) || 0), 0);
  const totIndex = sched.reduce((a, r) => a + (Number(r.index_volume) || 0), 0);
  const totVol = totBlock + totIndex;
  const avgHedge = totVol ? (totBlock / totVol) * 100 : 0;
  const avgIndexed = totVol ? (totIndex / totVol) * 100 : 0;
  const pieData = [{ name: 'Hedge (Block)', value: +avgHedge.toFixed(1) }, { name: 'Indexed', value: +avgIndexed.toFixed(1) }];
  const spendData = sched.filter((r) => r.month).map((r) => ({ month: r.month, Block: Number(r.block_volume) || 0, Index: Number(r.index_volume) || 0 }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold"><DollarSign className="w-4 h-4" /> Contract Pricing <span className="text-xs font-normal text-muted-foreground capitalize">· {type.replace('_', ' & ')}</span></div>
        {canEdit && <Button size="sm" onClick={save} disabled={saving} data-testid="pricing-save-btn"><Save className="w-4 h-4 mr-1.5" />{saving ? 'Saving…' : 'Save Pricing'}</Button>}
      </div>

      {type === 'fixed' && (
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <F label="All-in Rate"><Input type="number" step="0.0001" value={fixed.rate} disabled={!canEdit} onChange={(e) => setFixed((s) => ({ ...s, rate: e.target.value }))} data-testid="pricing-fixed-rate" /></F>
          <F label="Unit"><Input value={fixed.uom} disabled={!canEdit} onChange={(e) => setFixed((s) => ({ ...s, uom: e.target.value }))} /></F>
        </div>
      )}

      {type === 'index' && (
        <div className="space-y-3 max-w-2xl">
          <div className="border rounded-md p-3 bg-muted/30 space-y-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Live Market Index</Label>
              <LiveFeedChip feed={feedBySymbol(index.index_feed_symbol)} />
            </div>
            <FeedSelect value={index.index_feed_symbol} onChange={onPickFeed(setIndex)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Index Reference"><Input value={index.index_ref} disabled={!canEdit} onChange={(e) => setIndex((s) => ({ ...s, index_ref: e.target.value }))} placeholder="e.g. ERCOT North LMP, Henry Hub" data-testid="pricing-index-ref" /></F>
            <F label="Adder / Margin"><Input type="number" step="0.0001" value={index.adder} disabled={!canEdit} onChange={(e) => setIndex((s) => ({ ...s, adder: e.target.value }))} data-testid="pricing-index-adder" /></F>
            <F label="% Index Exposure"><Input type="number" value={index.pct_index_exposure} disabled={!canEdit} onChange={(e) => setIndex((s) => ({ ...s, pct_index_exposure: e.target.value }))} /></F>
            <F label="Unit"><Input value={index.uom} disabled={!canEdit} onChange={(e) => setIndex((s) => ({ ...s, uom: e.target.value }))} /></F>
          </div>
        </div>
      )}

      {type === 'block_index' && (
        <div className="space-y-5">
          <div className="border rounded-md p-3 bg-muted/30 space-y-2 max-w-3xl">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Live Market Index</Label>
              <LiveFeedChip feed={feedBySymbol(bi.index_feed_symbol)} />
            </div>
            <FeedSelect value={bi.index_feed_symbol} onChange={onPickFeed(setBi)} />
          </div>
          <div className="grid grid-cols-3 gap-3 max-w-3xl">
            <F label="Block Rate ($/unit)"><Input type="number" step="0.0001" value={bi.block_rate} disabled={!canEdit} onChange={(e) => setBi((s) => ({ ...s, block_rate: e.target.value }))} data-testid="pricing-block-rate" /></F>
            <F label="Index Reference"><Input value={bi.index_ref} disabled={!canEdit} onChange={(e) => setBi((s) => ({ ...s, index_ref: e.target.value }))} placeholder="e.g. LMP" data-testid="pricing-bi-index-ref" /></F>
            <F label="Index Adder ($/unit)"><Input type="number" step="0.0001" value={bi.index_adder} disabled={!canEdit} onChange={(e) => setBi((s) => ({ ...s, index_adder: e.target.value }))} /></F>
          </div>

          {/* Analytics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="border rounded-md p-4 bg-emerald-50/50">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Average Hedge</div>
              <div className="text-3xl font-bold text-emerald-700">{avgHedge.toFixed(1)}%</div>
              <div className="text-xs text-muted-foreground mt-1">Average Indexed {avgIndexed.toFixed(1)}%</div>
            </div>
            <div className="border rounded-md p-3 flex items-center justify-center">
              {totVol > 0 ? (
                <ResponsiveContainer width="100%" height={140}>
                  <PieChart><Pie data={pieData} dataKey="value" nameKey="name" innerRadius={30} outerRadius={55}>
                    <Cell fill="#10b981" /><Cell fill="#f59e0b" />
                  </Pie><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} /></PieChart>
                </ResponsiveContainer>
              ) : <span className="text-xs text-muted-foreground">Add schedule rows to see hedge mix</span>}
            </div>
            <div className="border rounded-md p-3">
              {spendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={140}>
                  <BarChart data={spendData}><CartesianGrid strokeDasharray="3 3" stroke="#eee" /><XAxis dataKey="month" tick={{ fontSize: 9 }} /><YAxis tickFormatter={compact} tick={{ fontSize: 9 }} /><Tooltip formatter={(v) => Number(v).toLocaleString()} /><Bar dataKey="Block" stackId="a" fill="#f59e0b" /><Bar dataKey="Index" stackId="a" fill="#10b981" /></BarChart>
                </ResponsiveContainer>
              ) : <span className="text-xs text-muted-foreground flex h-full items-center justify-center">Block vs Index volume</span>}
            </div>
          </div>

          {/* Schedule table */}
          <div className="border rounded-md overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/40">
              <span className="text-sm font-semibold">Contract Pricing Structure (Monthly)</span>
              {canEdit && <Button size="sm" variant="outline" onClick={addRow} data-testid="pricing-add-row"><Plus className="w-3.5 h-3.5 mr-1" /> Add Month</Button>}
            </div>
            <table className="w-full text-sm" data-testid="pricing-schedule-table">
              <thead><tr className="border-b text-xs text-muted-foreground">
                <th className="text-left p-2">Month</th><th className="text-left p-2">Hedge %</th><th className="text-left p-2">Block Vol</th><th className="text-left p-2">Index Vol</th><th className="text-left p-2">Total</th>{canEdit && <th className="w-10" />}
              </tr></thead>
              <tbody>
                {sched.length === 0 && <tr><td colSpan={canEdit ? 6 : 5} className="p-4 text-center text-muted-foreground text-sm">No schedule rows yet.</td></tr>}
                {sched.map((r, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-1"><Input className="h-8" type="month" value={r.month} disabled={!canEdit} onChange={(e) => setRow(i, 'month', e.target.value)} /></td>
                    <td className="p-1"><Input className="h-8" type="number" value={r.hedge_pct} disabled={!canEdit} onChange={(e) => setRow(i, 'hedge_pct', e.target.value)} /></td>
                    <td className="p-1"><Input className="h-8" type="number" value={r.block_volume} disabled={!canEdit} onChange={(e) => setRow(i, 'block_volume', e.target.value)} /></td>
                    <td className="p-1"><Input className="h-8" type="number" value={r.index_volume} disabled={!canEdit} onChange={(e) => setRow(i, 'index_volume', e.target.value)} /></td>
                    <td className="p-1"><Input className="h-8" type="number" value={r.total} disabled={!canEdit} onChange={(e) => setRow(i, 'total', e.target.value)} /></td>
                    {canEdit && <td className="p-1 text-center"><button onClick={() => delRow(i)} data-testid={`pricing-del-row-${i}`}><Trash2 className="w-4 h-4 text-destructive" /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default PricingSection;
